/** Checking the trip and choosing how to pay, then booking it. */

import { ApiError, errorMessage, get, newIdempotencyKey, post } from "@taxi/mobile-web/api";
import { when as formatWhen, money } from "@taxi/mobile-web/format";
import { useResource } from "@taxi/mobile-web/hooks";
import {
  BagIcon,
  BriefcaseIcon,
  CardIcon,
  CashIcon,
  ClockIcon,
  UsersIcon,
} from "@taxi/mobile-web/icons";
import { navigate } from "@taxi/mobile-web/router";
import {
  Button,
  Card,
  Choice,
  ChoiceGroup,
  Field,
  Notice,
  Stepper,
  TextArea,
  TopBar,
  useToast,
} from "@taxi/mobile-web/ui";
import { useEffect, useRef, useState } from "react";
import { VehicleArt } from "../components/vehicle-art";
import { placeTitle, useDraft } from "../lib/draft";
import { useMe, useSession } from "../lib/session";
import type { CreateBookingResponse, PaymentMethod, SavedCard } from "../lib/types";
import { savePaymentSheet } from "./pay";

const brandName = (brand: string) => brand.charAt(0).toUpperCase() + brand.slice(1);

export function ConfirmScreen() {
  const { firm } = useSession();
  const me = useMe();
  const toast = useToast();
  const { draft, update, reset } = useDraft();
  const quote = draft.quotes.find((q) => q.id === draft.quoteId) ?? null;
  const cards = useResource(() => get<{ data: SavedCard[] }>("/customer/payment-methods"), []);
  const [someoneElse, setSomeoneElse] = useState(Boolean(draft.passengerName));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotencyKey = useRef(newIdempotencyKey());

  useEffect(() => {
    if (!quote) navigate("/book/quotes", { replace: true });
  }, [quote]);

  const accountApproved = me.account?.status === "approved";
  const methods: {
    value: PaymentMethod;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
  }[] = [];
  const firstCard = cards.data?.data[0];
  if (firm.payments.card) {
    methods.push({
      value: "card",
      title: "Card",
      subtitle: firstCard
        ? `${brandName(firstCard.brand)} •••• ${firstCard.last4}, or another card`
        : "Debit or credit card, Apple Pay or Google Pay",
      icon: <CardIcon />,
    });
  }
  if (firm.payments.cash) {
    methods.push({
      value: "cash",
      title: "Cash",
      subtitle: "Pay the driver at the end",
      icon: <CashIcon />,
    });
  }
  if (firm.payments.account && accountApproved) {
    methods.push({
      value: "account",
      title: "Pay on Account",
      subtitle: "Added to your account's next invoice",
      icon: <BriefcaseIcon />,
    });
  }
  const method = methods.some((m) => m.value === draft.paymentMethod)
    ? draft.paymentMethod
    : (methods[0]?.value ?? null);

  if (!quote || !draft.pickup || !draft.dropoff) return null;
  const type = firm.vehicleTypes.find((t) => t.id === quote.vehicleType.id);
  const stops = draft.stops.filter((s) => s.address);

  async function book() {
    if (!method || !quote) return;
    setBusy(true);
    setError(null);
    try {
      const result = await post<CreateBookingResponse>(
        "/customer/bookings",
        {
          quoteId: quote.id,
          paymentMethod: method,
          notes: draft.notes.trim() || null,
          passengerName: someoneElse ? draft.passengerName.trim() || null : null,
          luggage: draft.luggage,
          accountReference: method === "account" ? draft.accountReference.trim() || null : null,
        },
        { idempotencyKey: idempotencyKey.current },
      );
      const { booking, paymentSheet } = result;
      reset();
      if (booking.status === "pending_payment" && paymentSheet) {
        savePaymentSheet(booking.id, paymentSheet);
        navigate(`/bookings/${booking.id}/pay`, { replace: true });
      } else {
        navigate(`/bookings/${booking.id}`, { replace: true });
      }
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.code === "quote_expired") {
        toast("Prices have been updated. Please check and choose again.");
        update({ quotes: [], quoteId: null });
        navigate("/book/quotes", { replace: true });
        return;
      }
      setError(errorMessage(err));
    }
  }

  return (
    <div className="confirm">
      <TopBar title="Confirm your booking" back="/book/quotes" />
      <div className="screen-body">
        <Card className="confirm-trip">
          <div className="confirm-vehicle">
            <VehicleArt icon={quote.vehicleType.icon} size={84} />
            <div>
              <strong>{quote.vehicleType.name}</strong>
              <span className="meta">
                <span>
                  <ClockIcon size={14} />
                  {quote.isAsap ? "Now" : formatWhen(new Date(quote.pickupAt))}
                </span>
                <span>
                  <UsersIcon size={14} /> {draft.passengers}
                </span>
              </span>
            </div>
            <span className="confirm-price">
              {money(quote.pricePence)}
              {quote.isEstimate && <small>estimate</small>}
            </span>
          </div>
          <ol className="route-list">
            <li className="pickup">{placeTitle(draft.pickup)}</li>
            {stops.map((stop) => (
              <li key={`${stop.lat},${stop.lng}`} className="stop">
                {placeTitle(stop)}
              </li>
            ))}
            <li className="dropoff">{placeTitle(draft.dropoff)}</li>
          </ol>
        </Card>

        <h2 className="section-title">How you'll pay</h2>
        <ChoiceGroup label="Payment method">
          {methods.map((m) => (
            <Choice
              key={m.value}
              name="payment-method"
              checked={method === m.value}
              onSelect={() => update({ paymentMethod: m.value })}
              icon={m.icon}
              title={m.title}
              subtitle={m.subtitle}
            />
          ))}
        </ChoiceGroup>
        {methods.length === 0 && (
          <Notice tone="warning">
            Booking in the app isn't available right now. Please call us.
          </Notice>
        )}
        {firm.payments.account && !me.account && (
          <button
            type="button"
            className="text-link"
            onClick={() => navigate("/account/pay-on-account")}
          >
            Booking for a business? Ask for Pay on Account
          </button>
        )}
        {method === "account" && (
          <Field
            label="Reference (optional)"
            hint="A PO number, cost centre or the passenger's name, for the invoice"
            value={draft.accountReference}
            maxLength={100}
            onChange={(e) => update({ accountReference: e.target.value })}
          />
        )}

        <h2 className="section-title">For the driver</h2>
        <TextArea
          label="Note for the driver (optional)"
          placeholder="e.g. Ring when outside, or which entrance"
          maxLength={500}
          value={draft.notes}
          onChange={(e) => update({ notes: e.target.value })}
        />
        {type && type.luggage > 0 && (
          <Stepper
            label="Bags"
            icon={<BagIcon size={20} />}
            value={draft.luggage}
            min={0}
            max={type.luggage}
            onChange={(luggage) => update({ luggage })}
          />
        )}
        <label className="toggle">
          <input
            type="checkbox"
            checked={someoneElse}
            onChange={(e) => setSomeoneElse(e.target.checked)}
          />
          <span>I'm booking for someone else</span>
        </label>
        {someoneElse && (
          <Field
            label="Passenger's name"
            value={draft.passengerName}
            maxLength={100}
            onChange={(e) => update({ passengerName: e.target.value })}
          />
        )}

        {quote.lines.length > 0 && (
          <details className="breakdown">
            <summary>Price breakdown</summary>
            <dl>
              {quote.lines.map((line, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: fare lines have no ids and can repeat.
                <div key={`${line.kind}-${i}`}>
                  <dt>{line.label}</dt>
                  <dd>{money(line.amountPence)}</dd>
                </div>
              ))}
              <div className="total">
                <dt>Total</dt>
                <dd>{money(quote.pricePence)}</dd>
              </div>
            </dl>
            {firm.fares.vatEnabled && <p className="fine-print">Prices include VAT.</p>}
          </details>
        )}
        <p className="fine-print">Free to cancel any time before you're picked up.</p>
        {error && <Notice tone="error">{error}</Notice>}
      </div>
      <div className="footer-bar">
        <Button
          loading={busy}
          disabled={!method || (someoneElse && !draft.passengerName.trim())}
          onClick={book}
          value={money(quote.pricePence)}
        >
          {method === "card" ? "Book and pay" : "Book"}
        </Button>
      </div>
    </div>
  );
}
